import * as React from 'react';

// Keep the screen awake while `active` is true, via the Screen Wake Lock API.
// The lock is auto-released by the browser when the tab is hidden, so it's
// re-acquired on visibilitychange. Graceful no-op where the API is unsupported.
export function useWakeLock(active: boolean): void {
  React.useEffect(() => {
    if (!active) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;

    let sentinel: any = null;
    let cancelled = false;

    const request = async () => {
      try {
        const lock = await (navigator as any).wakeLock.request('screen');
        if (cancelled) {
          void lock.release?.();
          return;
        }
        sentinel = lock;
        sentinel?.addEventListener?.('release', () => {
          sentinel = null;
        });
      } catch {
        /* denied / not allowed — ignore */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !cancelled && !sentinel) {
        void request();
      }
    };

    void request();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try {
        void sentinel?.release?.();
      } catch {
        /* ignore */
      }
      sentinel = null;
    };
  }, [active]);
}
